import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ArrowLeft, Loader2, Save, Eye } from "lucide-react";
import {
  fetchCertificateTemplateById,
  fetchDefaultTemplateConfig,
  createCertificateTemplate,
  updateCertificateTemplate,
  clearCurrentTemplate,
} from "../../redux/slices/certificateTemplateSlice";
import { uploadAdminFile } from "../../redux/slices/fileSlice";
import CertificateTemplatePreview from "./CertificateTemplatePreview";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { useToast } from "../../components/ui/use-toast";
import api from "../../services/api";

const TYPES = [
  { value: "participation", label: "Participation" },
  { value: "completion", label: "Completion" },
  { value: "position_1", label: "1st Place" },
  { value: "position_2", label: "2nd Place" },
  { value: "position_3", label: "3rd Place" },
];

function CertificateTemplateEditor() {
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { toast } = useToast();
  const { currentTemplate, loading } = useSelector((s) => s.certificateTemplate);

  const [name, setName] = useState("");
  const [type, setType] = useState("completion");
  const [isActive, setIsActive] = useState(true);
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const bgInputRef = React.useRef(null);
  const logoInputRef = React.useRef(null);
  const sigInputRef = React.useRef(null);

  useEffect(() => {
    if (!isNew && id) {
      dispatch(fetchCertificateTemplateById(id));
    }
    return () => dispatch(clearCurrentTemplate());
  }, [dispatch, id, isNew]);

  useEffect(() => {
    if (isNew && !config) {
      dispatch(fetchDefaultTemplateConfig(type)).then((res) => {
        if (res.payload) setConfig(res.payload);
      });
    }
  }, [isNew, type, config, dispatch]);

  useEffect(() => {
    if (!isNew && currentTemplate) {
      setName(currentTemplate.name);
      setType(currentTemplate.type);
      setIsActive(!!currentTemplate.is_active);
      setConfig(currentTemplate.template_config || {});
    }
  }, [isNew, currentTemplate]);

  useEffect(() => {
    if (isNew && config) {
      dispatch(fetchDefaultTemplateConfig(type)).then((res) => {
        if (res.payload) {
          setConfig((prev) => ({
            ...res.payload,
            background: prev?.background || res.payload.background,
            logo_url: prev?.logo_url,
            signature_url: prev?.signature_url,
            colors: { ...res.payload.colors, ...(prev?.colors || {}) },
          }));
        }
      });
    }
    // Only when type changes on new template
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, isNew]);

  const updateConfig = (patch) => {
    setConfig((prev) => ({ ...prev, ...patch }));
  };

  const updatePlaceholder = (key, value) => {
    setConfig((prev) => ({
      ...prev,
      placeholders: { ...(prev?.placeholders || {}), [key]: value },
    }));
  };

  const updateColor = (key, value) => {
    setConfig((prev) => ({
      ...prev,
      colors: { ...(prev?.colors || {}), [key]: value },
    }));
  };

  const uploadImage = async (file, target) => {
    const result = await dispatch(
      uploadAdminFile({ file, category: "content_media" }),
    ).unwrap();
    const url = result?.file_url || result?.url;
    if (!url) throw new Error("Upload did not return a URL");

    if (target === "background") {
      updateConfig({
        background: {
          ...(config?.background || {}),
          type: "image",
          image_url: url,
        },
      });
    } else if (target === "logo") {
      updateConfig({ logo_url: url });
    } else if (target === "signature") {
      updateConfig({ signature_url: url });
    }
    toast({ title: "Image uploaded" });
  };

  const onFilePick = async (e, target) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      await uploadImage(file, target);
    } catch (err) {
      toast({
        title: "Upload failed",
        description: String(err),
        variant: "destructive",
      });
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim() || !config) return;
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        type,
        template_config: config,
        is_active: isActive,
      };
      if (isNew) {
        const created = await dispatch(createCertificateTemplate(payload)).unwrap();
        toast({ title: "Created" });
        navigate(`/certificate-templates/${created.id}`, { replace: true });
      } else {
        await dispatch(updateCertificateTemplate({ id, data: payload })).unwrap();
        toast({ title: "Saved" });
      }
    } catch (err) {
      toast({
        title: "Error",
        description: typeof err === "string" ? err : "Save failed",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const previewPdf = async () => {
    if (!id || isNew) return;
    try {
      const response = await api.get(
        `/admin/certificate-templates/${id}/preview`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(response.data);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast({
        title: "Preview failed",
        description: String(err),
        variant: "destructive",
      });
    }
  };

  if ((loading && !isNew && !currentTemplate) || !config) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Button variant="ghost" asChild>
        <Link to="/certificate-templates">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Templates
        </Link>
      </Button>

      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">
          {isNew ? "New certificate template" : name}
        </h1>
        <div className="flex gap-2">
          {!isNew && (
            <Button type="button" variant="outline" onClick={previewPdf}>
              <Eye className="w-4 h-4 mr-2" />
              PDF preview
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save
          </Button>
        </div>
      </div>

      <form onSubmit={handleSave} className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Basics</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4">
              <div className="grid gap-2">
                <Label>Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="grid gap-2">
                <Label>Type</Label>
                <Select value={type} onValueChange={setType} disabled={!isNew}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={isActive} onCheckedChange={setIsActive} />
                <Label>Active</Label>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Background & branding</CardTitle>
              <CardDescription>
                Upload a background image or set a solid color. Logo and signature
                appear on the certificate.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4">
              <div className="grid gap-2">
                <Label>Background type</Label>
                <Select
                  value={config.background?.type || "color"}
                  onValueChange={(v) =>
                    updateConfig({
                      background: { ...(config.background || {}), type: v },
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="color">Solid color</SelectItem>
                    <SelectItem value="image">Image</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {config.background?.type !== "image" ? (
                <div className="grid gap-2">
                  <Label>Background color</Label>
                  <Input
                    type="color"
                    value={config.background?.color || "#ffffff"}
                    onChange={(e) =>
                      updateConfig({
                        background: {
                          ...(config.background || {}),
                          type: "color",
                          color: e.target.value,
                        },
                      })
                    }
                  />
                </div>
              ) : (
                <div className="grid gap-2">
                  <Label>Background image</Label>
                  <input
                    ref={bgInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => onFilePick(e, "background")}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => bgInputRef.current?.click()}
                  >
                    Upload background
                  </Button>
                  {config.background?.image_url && (
                    <p className="text-xs text-muted-foreground truncate">
                      {config.background.image_url}
                    </p>
                  )}
                </div>
              )}
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs">Primary</Label>
                  <Input
                    type="color"
                    value={config.colors?.primary || "#1a73e8"}
                    onChange={(e) => updateColor("primary", e.target.value)}
                  />
                </div>
                <div>
                  <Label className="text-xs">Secondary</Label>
                  <Input
                    type="color"
                    value={config.colors?.secondary || "#5f6368"}
                    onChange={(e) => updateColor("secondary", e.target.value)}
                  />
                </div>
                <div>
                  <Label className="text-xs">Accent</Label>
                  <Input
                    type="color"
                    value={config.colors?.accent || "#fbbc04"}
                    onChange={(e) => updateColor("accent", e.target.value)}
                  />
                </div>
              </div>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => onFilePick(e, "logo")}
              />
              <input
                ref={sigInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => onFilePick(e, "signature")}
              />
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => logoInputRef.current?.click()}
                >
                  Upload logo
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => sigInputRef.current?.click()}
                >
                  Upload signature
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Text content</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3">
              {[
                ["header", "Header (e.g. CERTIFICATE)"],
                ["certificate_title", "Subtitle type (e.g. OF COMPLETION)"],
                ["subtitle", "Lead-in line"],
                ["body_text", "Body line after name"],
                ["signature_label", "Signature label"],
              ].map(([key, label]) => (
                <div key={key} className="grid gap-1">
                  <Label className="text-xs">{label}</Label>
                  <Input
                    value={config.placeholders?.[key] || ""}
                    onChange={(e) => updatePlaceholder(key, e.target.value)}
                  />
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <CertificateTemplatePreview
            config={config}
            sampleData={{
              // Preview-only placeholder — not a real user
              user_name: "Sample Student",
              course_name: "Sample Course Name",
              percentage: 88,
              taught_by: "Sample Instructor",
            }}
          />
          <p className="text-xs text-muted-foreground -mt-2">
            Preview uses sample names only. Real certificates use each learner&apos;s
            profile name when generated.
          </p>

          <Card>
            <CardHeader>
              <CardTitle>Layout elements</CardTitle>
              <CardDescription>
                Fine-tune positions (842×595 pt canvas). Changes reflect in preview.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="text">
                <TabsList>
                  <TabsTrigger value="text">Text blocks</TabsTrigger>
                </TabsList>
                <TabsContent value="text" className="max-h-96 overflow-y-auto space-y-3 mt-3">
                  {(config.elements || [])
                    .map((el, idx) => ({ el, idx }))
                    .filter(({ el }) => el.type === "text")
                    .map(({ el, idx }) => (
                      <div
                        key={idx}
                        className="border rounded p-3 grid grid-cols-3 gap-2 text-xs"
                      >
                        <span className="col-span-3 font-medium">
                          {el.field || el.content?.slice(0, 30) || `Text ${idx}`}
                        </span>
                        <div>
                          <Label className="text-[10px]">Y</Label>
                          <Input
                            type="number"
                            value={el.y}
                            onChange={(e) => {
                              const elements = [...config.elements];
                              elements[idx] = {
                                ...el,
                                y: Number(e.target.value),
                              };
                              updateConfig({ elements });
                            }}
                          />
                        </div>
                        <div>
                          <Label className="text-[10px]">Font</Label>
                          <Input
                            type="number"
                            value={el.fontSize}
                            onChange={(e) => {
                              const elements = [...config.elements];
                              elements[idx] = {
                                ...el,
                                fontSize: Number(e.target.value),
                              };
                              updateConfig({ elements });
                            }}
                          />
                        </div>
                        <div>
                          <Label className="text-[10px]">Color</Label>
                          <Input
                            type="color"
                            value={
                              el.color?.startsWith("#")
                                ? el.color
                                : "#333333"
                            }
                            onChange={(e) => {
                              const elements = [...config.elements];
                              elements[idx] = { ...el, color: e.target.value };
                              updateConfig({ elements });
                            }}
                          />
                        </div>
                      </div>
                    ))}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </form>
    </div>
  );
}

export default CertificateTemplateEditor;
